import type { IProductCard } from '@src/components/product-card/product-card.types'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import type { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import type { SvdCardToken, SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

/**
 * SVD anchor names are camelCase wire values (`'color'`, `'size'`, `'pattern'`, `'fabrication'`); the legacy
 * enriched product keys its options off `ProductOptionName` (`'Color'`, `'Size'`). Only the anchors that have a
 * legacy counterpart can seed a selection — anything else has no representation in the legacy option shape and
 * must be dropped rather than cast through.
 */
const LEGACY_OPTION_NAME: Record<string, ProductOptionName> = {
  color: ProductOptionName.Color,
  size: ProductOptionName.Size,
}

/**
 * Path-A quick-add payload for the existing enrich-based `ProductBuyNow` modal, built from the SVD
 * card's native data.
 *
 * The modal + `useFullProductInterceptor` read only two fields off `productCard` before enriching:
 * `product.relatedIds.spid` (the fetch key) and `product.productId`. Everything the modal renders
 * (sizes, variants, inventory, price) comes from the `/products/enrich` response, not this object —
 * so this is a deliberately minimal, ids-only shim, cast to `IProductCard` at the boundary and
 * **never rendered**. It is a controlled, single-purpose Option-2 exception (the SVD flow otherwise
 * shares no shape with the legacy `IProductCard`/`CardVariant`).
 *
 * `svdCardToken` is carried for Path B (the SVD-native buy box): when that ships, the modal router
 * resolves the full product by internal `productId` + `selectedAttributes` instead of enrich-by-spid.
 * It is ignored on Path A. See `product-buy-now-ds/index.tsx`.
 */
export interface SvdQuickAddPayload {
  productCard: IProductCard
  selectedOptions: ProductVariantOption[]
  svdCardToken: SvdCardToken
}

export function buildSvdQuickAddPayload(
  product: SvdNativeProduct,
  selectedVariant: SvdNativeCardVariant | undefined,
): SvdQuickAddPayload {
  // The SELECTED swatch's shoppable id is the enrich fetch key — in SVD each anchor value can map to
  // a distinct spid, so this (not the product-level spid) resolves the right variant's full product.
  const spid = selectedVariant?.spid ?? product.relatedIds.spid

  // Minimal ids-only shim. Only spid + productId are read pre-enrich; title/reviews are populated
  // defensively (cheap, and keep the modal's aria/label paths safe). Cast at the boundary — this
  // object is never rendered.
  const productCard = {
    product: {
      productId: product.productId,
      relatedIds: { contentfulId: product.relatedIds.contentfulId, spid },
      title: product.title,
      reviews: product.reviews,
      colorVariants: product.colorVariants,
      customVariants: product.customVariants,
    },
  } as unknown as IProductCard

  // Seed only, and only for anchors the legacy option shape can express (see `LEGACY_OPTION_NAME`).
  // For an unmapped anchor there is no legacy option to seed, so emit nothing rather than a name the
  // enriched product will never match — `getUpdatedSelectedOptions` then applies its own Color default.
  const displayConfig = selectedVariant?.displayConfig
  const legacyName = displayConfig && LEGACY_OPTION_NAME[displayConfig.name]
  const selectedOptions: ProductVariantOption[] =
    displayConfig && legacyName ? [{ name: legacyName, value: displayConfig.value }] : []

  const svdCardToken: SvdCardToken = {
    productId: String(product.productId),
    selectedAttributes: selectedVariant?.selectedAttributes ?? {},
  }

  return { productCard, selectedOptions, svdCardToken }
}
